import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageChannel,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { RedoTyping } from "@redotech/redo-web/arbiter-components/chat/redo-typing";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import type { DraftCollectionItem } from "../../db/collections";
import { useChatThreadView } from "../../hooks/use-chat-thread-view";
import { useSupportTicketPresence } from "../../hooks/use-support-ticket-data";
import { useSupportTicketDrafts } from "../../providers/support-ticket-drafts-context";
import { MessageInput } from "../message-input";
import { ChatMessageBubble } from "../message-types/chat-message-bubble";
import { SendingChatBubble } from "../message-types/sending-chat-bubble";
import { ThreadHeader } from "../thread-header";

const WIDGET_CUSTOMER_PRESENCE_USER_ID = "Customer";

interface ChatThreadViewProps {
  messages: Message[];
  supportTicketId: SupportTicketId;
  thread: ChannelThread;
  channel: MessageChannel;
  allowReplies?: boolean;
  messageThreadIds?: readonly ChannelThreadId[];
  renderMessage?: (
    message: Message,
    threadId: ChannelThreadId,
  ) => React.ReactNode;
}

export const ChatThreadView = memo(function ChatThreadView({
  messages,
  supportTicketId,
  thread,
  channel,
  allowReplies = true,
  messageThreadIds,
  renderMessage,
}: ChatThreadViewProps) {
  const threadIds = useMemo(
    () => messageThreadIds ?? [thread.id],
    [messageThreadIds, thread.id],
  );
  const threadIdSet = useMemo(
    () => new Set(threadIds.map(String)),
    [threadIds],
  );
  const { data: presenceUsers } = useSupportTicketPresence(supportTicketId);
  const { messageGroups, lastMessageId } = useChatThreadView(messages);
  const customerIsTyping = useMemo(
    () =>
      presenceUsers.some(
        (presenceUser) =>
          presenceUser.userId === WIDGET_CUSTOMER_PRESENCE_USER_ID &&
          presenceUser.isTyping,
      ),
    [presenceUsers],
  );

  // Get drafts from context (fetched once at support ticket level)
  const { drafts: allSupportTicketDrafts } = useSupportTicketDrafts();

  // Filter to drafts for this specific thread and find the sending one
  const sendingDraft = useMemo(
    () =>
      allSupportTicketDrafts.find(
        (d: DraftCollectionItem) =>
          d.kind === "merchant" &&
          threadIdSet.has(d.threadId) &&
          d.status === "SENDING",
      ),
    [allSupportTicketDrafts, threadIdSet],
  );

  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const scrollToBottom = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      node.scrollIntoView({ behavior: "instant" });
    }
  }, []);

  return (
    <Flex dir="column" gap="none" height="full">
      <ThreadHeader
        channel={channel}
        isCollapsed={isCollapsed}
        latestMessageTime={messages?.at(-1)?.sentAt}
        onClick={toggleCollapsed}
        subject={null}
      />
      {!isCollapsed && (
        <Flex
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderLeftWidth="1px"
          borderRightWidth="1px"
          borderStyle="solid"
          bottomLeftRadius="sm"
          bottomRightRadius="sm"
          dir="column"
          gap="none"
          grow={1}
        >
          <Flex dir="column" gap="lg" grow={1} overflowY="auto" p="xl">
            {messageGroups.length === 0 ? (
              <Flex align="center" grow={1} justify="center" p="xl">
                <Text fontSize="md" textColor="secondary">
                  No messages in this thread
                </Text>
              </Flex>
            ) : (
              messageGroups.map((group) => (
                <Fragment key={group.dateLabel}>
                  <DateDivider label={group.dateLabel} />
                  {group.messages.map((formattedMessage) =>
                    renderMessage ? (
                      <Fragment key={formattedMessage.id}>
                        {renderMessage(
                          formattedMessage,
                          formattedMessage.channelThreadId,
                        )}
                      </Fragment>
                    ) : (
                      <ChatMessageBubble
                        key={formattedMessage.id}
                        message={formattedMessage}
                      />
                    ),
                  )}
                </Fragment>
              ))
            )}
            {sendingDraft && <SendingChatBubble draft={sendingDraft} />}
            <div ref={scrollToBottom} />
          </Flex>
          {customerIsTyping && <CustomerTypingIndicator />}
          {allowReplies && (
            <Flex
              bgColor="primary"
              borderColor="secondary"
              borderStyle="solid"
              borderTopWidth="1px"
              p="xl"
            >
              <MessageInput
                channel={channel}
                inReplyToMessageId={lastMessageId ?? undefined}
                mode="chat"
                supportTicketId={supportTicketId}
                threadId={thread.id}
              />
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
});

const CustomerTypingIndicator = memo(function CustomerTypingIndicator() {
  return (
    <Flex align="center" bgColor="primary" pb="md" pt="xs" px="xl" w="full">
      <RedoTyping size="sm" />
    </Flex>
  );
});

const DateDivider = memo(function DateDivider({ label }: { label: string }) {
  return (
    <Flex align="center" gap="md" w="full">
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
      <Text fontSize="xs" textColor="tertiary" whiteSpace="nowrap">
        {label}
      </Text>
      <Flex
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        grow={1}
      />
    </Flex>
  );
});
